import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Input,
  Button,
  Tooltip,
  Spin,
  Tag,
  Switch,
} from 'antd';
import {
  SendOutlined,
  RobotOutlined,
  ThunderboltOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import { useEstimateFormContext, type AISuggestion } from '../contexts/EstimateFormContext';
import { getTokens } from '../services/authApi';
import { logEvent, newEventId } from '../services/eventLogger';
import CustomerMatcher from '../utils/customerMatcher';
import VehicleMatcher from '../utils/vehicleMatcher';
import { vehicleApi } from '../services/api';
import AIDataTransformer from '../utils/aiDataTransformer';
import type { Customer, Vehicle, EstimateLineItem } from '../types';

const { TextArea } = Input;

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:3001/api';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface CustomerSearchTerms {
  name?: string | null;
  phone?: string | null;
  email?: string | null;
}

interface VehicleSearchTerms {
  year?: string | number | null;
  make?: string | null;
  model?: string | null;
}

interface CorrectionChange {
  type: 'field_update' | 'service_item_update' | 'add_service_item' | 'remove_service_item' | 'customer_search' | 'vehicle_search';
  target: string | number;
  field?: string;
  value?: unknown;
  search_terms?: CustomerSearchTerms & VehicleSearchTerms;
  explanation: string;
}

interface CorrectionResponse {
  changes: CorrectionChange[];
  follow_up: string | null;
  save_trigger: boolean;
  next_phase: string | null;
  provider: string;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  type: 'source_text' | 'analysis_result' | 'correction' | 'error' | 'greeting';
  changes?: CorrectionChange[];
  follow_up?: string | null;
  serviceItems?: any[];
  timestamp: Date;
}

export interface UnifiedAIChatProps {
  serviceItems: EstimateLineItem[];
  onAnalyze?: (data: any) => void;
  onCustomerAutoSelected?: (customer: Customer) => void;
  onCustomerCandidatesFound?: (candidates: any[]) => void;
  onVehicleAutoSelected?: (vehicle: Vehicle) => void;
  onVehicleCandidatesFound?: (candidates: any[]) => void;
  existingCustomerId?: number;
  onCustomerFieldChange: (fieldPath: string, value: unknown) => void;
  onVehicleFieldChange: (fieldPath: string, value: unknown) => void;
  onServiceItemChange: (index: number, field: string, value: unknown) => void;
  onAddServiceItem: (item: Partial<EstimateLineItem>) => void;
  onRemoveServiceItem: (index: number) => void;
  onSave?: () => Promise<void>;
  onCustomerSearch?: (terms: CustomerSearchTerms) => Promise<string | null>;
  onVehicleSearch?: (terms: { year?: string | number | null; make?: string | null; model?: string | null }) => Promise<string | null>;
  hasCustomer?: boolean;
  disabled?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function buildAnalysisSummary(data: any, serviceItems: any[]): string {
  const parts: string[] = [];

  if (serviceItems.length > 0) {
    parts.push(`Found ${serviceItems.length} service item${serviceItems.length !== 1 ? 's' : ''}`);
    const preview = serviceItems.slice(0, 3).map((si: any) => {
      const hours = si.labor_hours != null ? ` ${si.labor_hours}h` : '';
      return (si.description?.substring(0, 40) ?? 'Item') + hours;
    });
    parts.push(preview.join(', '));
  } else {
    parts.push('No service items detected');
  }

  const missingFields = Object.entries(data.fields || {})
    .filter(([, f]: [string, any]) => f.band === 'required_missing')
    .map(([path]: [string, any]) =>
      path.replace('customer.', '').replace('vehicle.', '').replace(/_/g, ' ')
    );

  if (missingFields.length > 0) {
    parts.push(`Missing: ${missingFields.join(', ')}`);
  }

  return parts.join(' · ');
}

function makeId(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// ─────────────────────────────────────────────────────────────────────────────
// ProcedureStepsDisplay — inline sub-component
// Shows an expandable, editable list of step-by-step labour hours for one
// service item. Each step's hours can be clicked to edit; the parent is
// notified of the new total so it can update the form state.
// ─────────────────────────────────────────────────────────────────────────────

interface ProcedureStepsDisplayProps {
  steps: Array<{ step: string; hours: number }>;
  totalHours: number;
  onStepHoursChange: (newTotalHours: number) => void;
}

const ProcedureStepsDisplay: React.FC<ProcedureStepsDisplayProps> = ({
  steps: initialSteps,
  totalHours: initialTotal,
  onStepHoursChange,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [editedSteps, setEditedSteps] = useState(initialSteps.map(s => ({ ...s })));
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');

  const currentTotal = editedSteps.reduce((acc, s) => acc + s.hours, 0);
  const roundedTotal = Math.round(currentTotal * 10) / 10;
  const isEdited = Math.abs(currentTotal - initialTotal) > 0.05;

  const handleStepEdit = (index: number) => {
    setEditingIndex(index);
    setEditValue(String(editedSteps[index].hours));
  };

  const handleStepSave = (index: number) => {
    const newHours = parseFloat(editValue);
    if (!isNaN(newHours) && newHours > 0 && newHours <= 80) {
      const updated = [...editedSteps];
      updated[index] = { ...updated[index], hours: newHours };
      setEditedSteps(updated);
      const newTotal = Math.round(updated.reduce((acc, s) => acc + s.hours, 0) * 10) / 10;
      onStepHoursChange(newTotal);
    }
    setEditingIndex(null);
  };

  const handleReset = () => {
    setEditedSteps(initialSteps.map(s => ({ ...s })));
    onStepHoursChange(initialTotal);
  };

  return (
    <div style={{ marginTop: '6px' }}>
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          cursor: 'pointer',
          fontSize: '11px',
          color: '#6366f1',
          background: '#eff6ff',
          border: '1px solid #bfdbfe',
          borderRadius: '12px',
          padding: '2px 8px',
          userSelect: 'none',
        }}
      >
        <span>Steps</span>
        <span>{editedSteps.length} steps · {roundedTotal}h</span>
        {isEdited && <span style={{ color: '#f59e0b' }}>(edited)</span>}
        <span style={{ fontSize: '9px', marginLeft: '2px' }}>{expanded ? '▲' : '▼'}</span>
      </div>

      {expanded && (
        <div style={{
          marginTop: '6px',
          background: '#f8faff',
          border: '1px solid #e0e7ff',
          borderRadius: '6px',
          padding: '6px',
          fontSize: '11px',
        }}>
          {editedSteps.map((s, idx) => (
            <div key={idx} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '3px 0',
              borderBottom: idx < editedSteps.length - 1 ? '1px solid #f0f0f0' : 'none',
            }}>
              <span style={{ color: '#9ca3af', minWidth: '16px', textAlign: 'right' }}>{idx + 1}.</span>
              <span style={{ flex: 1, color: '#374151', lineHeight: 1.3 }}>{s.step}</span>
              {editingIndex === idx ? (
                <input
                  type="number"
                  step="0.1"
                  min="0.1"
                  max="80"
                  value={editValue}
                  onChange={e => setEditValue(e.target.value)}
                  onBlur={() => handleStepSave(idx)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleStepSave(idx);
                    if (e.key === 'Escape') setEditingIndex(null);
                  }}
                  autoFocus
                  style={{
                    width: '48px',
                    fontSize: '11px',
                    border: '1px solid #6366f1',
                    borderRadius: '4px',
                    padding: '1px 4px',
                    textAlign: 'right',
                  }}
                />
              ) : (
                <button
                  onClick={() => handleStepEdit(idx)}
                  style={{
                    fontSize: '11px',
                    color: '#6366f1',
                    background: 'none',
                    border: '1px solid transparent',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    padding: '1px 4px',
                    minWidth: '36px',
                    textAlign: 'right',
                  }}
                  title="Click to edit"
                >
                  {s.hours}h
                </button>
              )}
            </div>
          ))}

          {isEdited && (
            <button
              onClick={handleReset}
              style={{
                marginTop: '4px',
                fontSize: '10px',
                color: '#9ca3af',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                textDecoration: 'underline',
                padding: 0,
              }}
            >
              Reset to AI estimate
            </button>
          )}

          <div style={{
            marginTop: '4px',
            paddingTop: '4px',
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'flex-end',
            fontWeight: 600,
            color: '#374151',
          }}>
            Total: {roundedTotal}h
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export const UnifiedAIChat: React.FC<UnifiedAIChatProps> = ({
  serviceItems,
  onAnalyze,
  onCustomerAutoSelected,
  onCustomerCandidatesFound,
  onVehicleAutoSelected,
  onVehicleCandidatesFound,
  existingCustomerId,
  onCustomerFieldChange,
  onVehicleFieldChange,
  onServiceItemChange,
  onAddServiceItem,
  onRemoveServiceItem,
  onSave,
  onCustomerSearch,
  onVehicleSearch,
  hasCustomer = true,
  disabled = false,
}) => {
  const ctx = useEstimateFormContext();
  const { focusedField, aiAnalysis } = ctx;

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasAnalyzed, setHasAnalyzed] = useState(false);

  // Mode — persisted to localStorage
  const [mode, setMode] = useState<'fast' | 'interactive'>(() => {
    try {
      return (localStorage.getItem('ai_chat_mode') as 'fast' | 'interactive') ?? 'fast';
    } catch {
      return 'fast';
    }
  });

  const bottomRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to newest message
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Persist mode
  useEffect(() => {
    try { localStorage.setItem('ai_chat_mode', mode); } catch { /* ignore */ }
  }, [mode]);

  // ── Mode toggle ─────────────────────────────────────────────────────────────

  const handleModeToggle = useCallback((checked: boolean) => {
    const newMode = checked ? 'interactive' : 'fast';
    setMode(newMode);

    if (newMode === 'interactive') {
      const itemCount = serviceItems.length;
      const greetingText = hasAnalyzed
        ? `Switched to Guided mode. I can see ${itemCount} service item${itemCount !== 1 ? 's' : ''}. Ask me anything or tell me what to correct.`
        : `Guided mode is on. Describe the customer, vehicle, and service needed — I'll walk you through each section.`;

      setMessages(prev => [
        ...prev,
        {
          id: makeId(),
          role: 'assistant',
          text: greetingText,
          type: 'greeting',
          timestamp: new Date(),
        },
      ]);
    } else {
      if (messages.length > 0) {
        setMessages(prev => [
          ...prev,
          {
            id: makeId(),
            role: 'assistant',
            text: 'Switched to Fast mode — form data is preserved.',
            type: 'greeting',
            timestamp: new Date(),
          },
        ]);
      }
    }
  }, [serviceItems.length, hasAnalyzed, messages.length]);

  // ── Analyze flow (first message → POST /analyze) ────────────────────────────

  const handleAnalyze = useCallback(async () => {
    const trimmed = input.trim();
    if (!trimmed) return;

    // Add user source text bubble immediately
    const userMsgId = makeId();
    const userMsg: ChatMessage = {
      id: userMsgId,
      role: 'user',
      text: trimmed,
      type: 'source_text',
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    ctx.startAIAnalysis();

    logEvent({
      event_id: newEventId(),
      event_type: 'ai.analyze.run',
      entity_type: 'ai',
      source: 'ai_analyze',
    });

    try {
      const tokens = getTokens();
      const response = await fetch(`${API_BASE_URL}/ai/estimate/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(tokens?.accessToken ? { Authorization: `Bearer ${tokens.accessToken}` } : {}),
        },
        body: JSON.stringify({
          source_text: trimmed,
          solved_fields: [],
          vehicle_context: ctx.vehicleData ? {
            id: (ctx.vehicleData as any).id ?? null,
            year: ctx.vehicleData.year ?? null,
            make: ctx.vehicleData.make ?? null,
            model: ctx.vehicleData.model ?? null,
            engine_size: (ctx.vehicleData as any).engine_size ?? null,
            transmission_type: (ctx.vehicleData as any).transmission_type ?? null,
            drive_type: (ctx.vehicleData as any).drive_type ?? null,
            fuel_type: (ctx.vehicleData as any).fuel_type ?? null,
            turbo: !!(ctx.vehicleData as any).turbo ?? false,
          } : null,
        }),
      });

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        let errText = errBody?.detail ?? errBody?.error?.message ?? 'AI analysis failed. Please try again.';
        if (response.status === 429) errText = 'AI analysis limit reached. Please wait a few minutes and try again.';
        if (response.status === 401) errText = 'Session expired. Please log in again.';
        // The shop's own master switch. "Try again" would be a loop with no end.
        if (errBody?.error?.code === 'ai_disabled' || errBody?.ai_enabled === false) {
          errText = 'AI is turned off for this shop. An owner can turn it back on in Settings → AI Features.';
        }

        ctx.errorAIAnalysis();
        setMessages(prev => [
          ...prev,
          {
            id: makeId(),
            role: 'assistant',
            text: errText,
            type: 'error',
            timestamp: new Date(),
          },
        ]);
        return;
      }

      const data = await response.json();

      // Build suggestions map
      const suggestions: Record<string, AISuggestion> = {};
      if (data.fields) {
        for (const [fieldPath, field] of Object.entries(data.fields as Record<string, any>)) {
          suggestions[fieldPath] = {
            value: field.value,
            confidence: field.confidence,
            band: field.band,
            alternatives: field.alternatives ?? [],
            autoApplied: false,
          };
        }
      }

      // ── Three-tier customer matching ────────────────────────────────────────
      let resolvedCustomerId: number | undefined = existingCustomerId;

      const custFirstName = String(suggestions['customer.first_name']?.value ?? '').trim();
      const custLastName  = String(suggestions['customer.last_name']?.value ?? '').trim();
      const custPhone     = String(suggestions['customer.phone']?.value ?? '').trim();

      if (custFirstName || custLastName || custPhone) {
        try {
          const result = await CustomerMatcher.findMatches(custFirstName, custLastName, custPhone);

          if (result.perfectMatch && onCustomerAutoSelected) {
            onCustomerAutoSelected(result.perfectMatch);
            resolvedCustomerId = result.perfectMatch.id;
          } else if (result.nameMatches.length > 0 && onCustomerCandidatesFound) {
            onCustomerCandidatesFound(result.nameMatches);
            resolvedCustomerId = result.nameMatches[0].customer.id;
          }
        } catch (err) {
          console.warn('[UnifiedAIChat] Customer matching failed, proceeding with new customer:', err);
        }
      }

      // ── Three-tier vehicle matching ─────────────────────────────────────────
      const vehYear  = String(suggestions['vehicle.year']?.value ?? '').trim();
      const vehMake  = String(suggestions['vehicle.make']?.value ?? '').trim();
      const vehModel = String(suggestions['vehicle.model']?.value ?? '').trim();

      if ((vehYear || vehMake || vehModel) && resolvedCustomerId) {
        try {
          const vResponse = await vehicleApi.getByCustomerId(resolvedCustomerId);
          const customerVehicles: Vehicle[] = vResponse.data || [];
          if (customerVehicles.length > 0) {
            const vResult = VehicleMatcher.findMatches(customerVehicles, vehYear, vehMake, vehModel);

            if (vResult.perfectMatch && onVehicleAutoSelected) {
              onVehicleAutoSelected(vResult.perfectMatch);
            } else if (vResult.closeMatches.length > 0 && onVehicleCandidatesFound) {
              onVehicleCandidatesFound(vResult.closeMatches);
            }
          }
        } catch (err) {
          console.warn('[UnifiedAIChat] Vehicle matching failed, proceeding with new vehicle:', err);
        }
      }

      // ── Dispatch to context ─────────────────────────────────────────────────
      const requiredMissingPaths = Object.entries(suggestions)
        .filter(([, s]) => s.band === 'required_missing')
        .map(([path]) => path);

      const rawServiceItems = data.service_items ?? [];

      console.log('[UnifiedAIChat] API returned service_items:', {
        count: rawServiceItems.length,
        items: rawServiceItems.map((si: any) => ({
          desc: si.description?.substring(0, 60),
          confidence: si.confidence,
          hours: si.labor_hours,
          rate: si.hourly_rate ?? si.suggested_rate,
          parts_count: (si.parts || []).length,
        })),
        provider: data.provider,
      });

      ctx.completeAIAnalysis({
        suggestions,
        requiredMissingPaths,
        allRequiredSatisfied: data.allRequiredSatisfied ?? false,
        provider: data.provider ?? 'groq',
        serviceItems: rawServiceItems,
      });

      if (rawServiceItems.length > 0) {
        logEvent({
          event_id: newEventId(),
          event_type: 'ai.analyze.service_items_extracted',
          entity_type: 'ai',
          source: 'ai_analyze',
          new_value: JSON.stringify({ service_items_count: rawServiceItems.length }),
          vehicle_year: Number(suggestions['vehicle.year']?.value) || undefined,
          vehicle_make: String(suggestions['vehicle.make']?.value ?? '').substring(0, 60) || undefined,
          vehicle_model: String(suggestions['vehicle.model']?.value ?? '').substring(0, 60) || undefined,
        });
      }

      // Legacy callback
      if (onAnalyze) {
        onAnalyze(data);
      }

      // Add AI analysis summary message (carries serviceItems for step display)
      const summaryText = buildAnalysisSummary(data, rawServiceItems);
      setMessages(prev => [
        ...prev,
        {
          id: makeId(),
          role: 'assistant',
          text: summaryText,
          type: 'analysis_result',
          serviceItems: rawServiceItems,
          timestamp: new Date(),
        },
      ]);

      setHasAnalyzed(true);
    } catch (error: any) {
      console.error('[UnifiedAIChat] analyze error:', error);
      ctx.errorAIAnalysis();
      setMessages(prev => [
        ...prev,
        {
          id: makeId(),
          role: 'assistant',
          text: 'Could not connect to AI service. Please try again.',
          type: 'error',
          timestamp: new Date(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  }, [
    input,
    ctx,
    existingCustomerId,
    onCustomerAutoSelected,
    onCustomerCandidatesFound,
    onVehicleAutoSelected,
    onVehicleCandidatesFound,
    onAnalyze,
  ]);

  // ── Apply correction changes ────────────────────────────────────────────────

  const applyChanges = useCallback(async (changes: CorrectionChange[]): Promise<string[]> => {
    const injectedMessages: string[] = [];
    for (const change of changes) {
      switch (change.type) {
        case 'field_update': {
          const target = String(change.target);
          if (target.startsWith('customer.')) {
            onCustomerFieldChange(target.replace('customer.', ''), change.value);
          } else if (target.startsWith('vehicle.')) {
            onVehicleFieldChange(target.replace('vehicle.', ''), change.value);
          }
          break;
        }
        case 'service_item_update': {
          const idx = typeof change.target === 'number'
            ? change.target
            : parseInt(String(change.target), 10);
          if (!isNaN(idx) && change.field) {
            onServiceItemChange(idx, change.field, change.value);
          }
          break;
        }
        case 'add_service_item': {
          const itemData = change.value as Partial<EstimateLineItem> | undefined;
          if (itemData) onAddServiceItem(itemData);
          break;
        }
        case 'remove_service_item': {
          const idx = typeof change.target === 'number'
            ? change.target
            : parseInt(String(change.target), 10);
          if (!isNaN(idx)) onRemoveServiceItem(idx);
          break;
        }
        case 'customer_search': {
          if (onCustomerSearch && change.search_terms) {
            const msg = await onCustomerSearch(change.search_terms);
            if (msg) injectedMessages.push(msg);
          }
          break;
        }
        case 'vehicle_search': {
          if (onVehicleSearch && change.search_terms) {
            const msg = await onVehicleSearch(change.search_terms);
            if (msg) injectedMessages.push(msg);
          }
          break;
        }
      }
    }
    return injectedMessages;
  }, [
    onCustomerFieldChange,
    onVehicleFieldChange,
    onServiceItemChange,
    onAddServiceItem,
    onRemoveServiceItem,
    onCustomerSearch,
    onVehicleSearch,
  ]);

  // ── Correction flow (subsequent messages → POST /correct) ──────────────────

  const sendCorrection = useCallback(async (messageText: string) => {
    if (!messageText.trim() || loading) return;

    const userMsg: ChatMessage = {
      id: makeId(),
      role: 'user',
      text: messageText.trim(),
      type: 'correction',
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    logEvent({
      event_id: newEventId(),
      event_type: 'ai.correct.message_sent',
      entity_type: 'ai',
      source: mode === 'interactive' ? 'ai_interactive' : 'ai_correct',
    });

    try {
      const tokens = getTokens();

      const customer = ctx.aiState.customerFieldsApproved
        ? { ...ctx.aiState.customerFieldsApproved }
        : ctx.customerData
        ? {
            first_name: ctx.customerData.first_name,
            last_name: ctx.customerData.last_name,
            phone: ctx.customerData.phone,
            email: ctx.customerData.email,
          }
        : {};

      const vehicle = ctx.aiState.vehicleFieldsApproved
        ? { ...ctx.aiState.vehicleFieldsApproved }
        : ctx.vehicleData
        ? {
            year: ctx.vehicleData.year,
            make: ctx.vehicleData.make,
            model: ctx.vehicleData.model,
            engine_size: (ctx.vehicleData as any).engine_size,
            current_mileage: ctx.vehicleData.current_mileage,
          }
        : {};

      const itemsForContext = serviceItems.map((si, idx) => ({
        id: si.id ?? idx,
        description: si.description,
        category: si.category,
        labor_hours: si.labor_hours ?? null,
        hourly_rate: si.hourly_rate ?? null,
        total_price: si.total_price,
      }));

      const body: Record<string, unknown> = {
        user_message: messageText.trim(),
        mode,
        focused_field: focusedField ?? null,
        current_form_state: {
          customer_id: existingCustomerId ?? null,
          vehicle_id: (ctx.vehicleData as any)?.id ?? null,
          customer,
          vehicle,
          service_items: itemsForContext,
        },
      };

      const response = await fetch(`${API_BASE_URL}/ai/estimate/correct`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(tokens?.accessToken ? { Authorization: `Bearer ${tokens.accessToken}` } : {}),
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail ?? `Server error ${response.status}`);
      }

      const data: CorrectionResponse = await response.json();

      const injectedMessages = await applyChanges(data.changes);

      const visibleChangeCount = data.changes.filter((c: any) => c.type !== 'customer_search').length;
      if (visibleChangeCount > 0) {
        logEvent({
          event_id: newEventId(),
          event_type: 'ai.correct.changes_applied',
          entity_type: 'ai',
          source: mode === 'interactive' ? 'ai_interactive' : 'ai_correct',
          new_value: JSON.stringify({ changes_count: visibleChangeCount }),
        });
      }

      const visibleChanges = data.changes.filter(c => c.type !== 'customer_search');
      const summaryLines = visibleChanges.map(c => `• ${c.explanation}`);
      const assistantText =
        summaryLines.length > 0
          ? summaryLines.join('\n')
          : mode === 'interactive'
          ? data.follow_up ?? "Got it. What's next?"
          : "I couldn't identify a specific change from that. Try rephrasing.";

      const newMessages: ChatMessage[] = [
        {
          id: makeId(),
          role: 'assistant',
          text: assistantText,
          type: 'correction',
          changes: data.changes,
          follow_up: data.follow_up,
          timestamp: new Date(),
        },
        ...injectedMessages.map(text => ({
          id: makeId(),
          role: 'assistant' as const,
          text,
          type: 'correction' as const,
          timestamp: new Date(),
        })),
      ];
      setMessages(prev => [...prev, ...newMessages]);

      // Handle save trigger from interactive mode
      if (data.save_trigger && onSave) {
        setMessages(prev => [
          ...prev,
          {
            id: makeId(),
            role: 'assistant',
            text: 'Saving estimate...',
            type: 'greeting',
            timestamp: new Date(),
          },
        ]);
        try {
          await onSave();
          setMessages(prev => [
            ...prev,
            {
              id: makeId(),
              role: 'assistant',
              text: 'Estimate saved as draft. Would you like to send it to the customer?',
              type: 'greeting',
              timestamp: new Date(),
            },
          ]);
        } catch {
          setMessages(prev => [
            ...prev,
            {
              id: makeId(),
              role: 'assistant',
              text: 'Save failed. Please use the Save button manually.',
              type: 'error',
              timestamp: new Date(),
            },
          ]);
        }
      }
    } catch (err: any) {
      setMessages(prev => [
        ...prev,
        {
          id: makeId(),
          role: 'assistant',
          text: err.message ?? 'Something went wrong. Please try again.',
          type: 'error',
          timestamp: new Date(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  }, [
    loading,
    mode,
    focusedField,
    ctx,
    serviceItems,
    applyChanges,
    onSave,
  ]);

  // ── Input key handler ───────────────────────────────────────────────────────

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!hasAnalyzed) {
        handleAnalyze();
      } else {
        sendCorrection(input);
      }
    }
  };

  const handleSend = () => {
    if (!hasAnalyzed) {
      handleAnalyze();
    } else {
      sendCorrection(input);
    }
  };

  // ── Focused field context label ─────────────────────────────────────────────

  const focusHintLabel = focusedField
    ? focusedField.path.replace('customer.', '').replace('vehicle.', '').replace(/_/g, ' ')
    : null;

  const isInteractive = mode === 'interactive';
  const hasMessages = messages.length > 0;

  // ── Placeholder text ────────────────────────────────────────────────────────

  const placeholder = !hasAnalyzed
    ? 'Describe the customer, vehicle, and service needed\u2026'
    : isInteractive
    ? 'Reply here\u2026 (Enter to send)'
    : focusHintLabel
    ? `Correct ${focusHintLabel}\u2026 (Enter to send)`
    : 'Ask a question or make a correction\u2026 (Enter to send)';

  // ─────────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        background: '#fafafe',
        borderRadius: '8px',
        border: '1px solid #e0e7ff',
        padding: '10px 12px',
        marginBottom: '16px',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {isInteractive
            ? <MessageOutlined style={{ color: '#6366f1', fontSize: '13px' }} />
            : <RobotOutlined style={{ color: '#6366f1', fontSize: '13px' }} />
          }
          <span style={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>
            AI Assistant
          </span>
          {isInteractive && (
            <Tag
              color="geekblue"
              style={{ fontSize: '10px', lineHeight: '16px', padding: '0 5px', margin: 0 }}
            >
              Guided
            </Tag>
          )}
        </div>

        {/* Mode toggle */}
        <Tooltip title={isInteractive ? 'Switch to Fast Mode' : 'Switch to Guided Mode (step-by-step)'}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <ThunderboltOutlined
              style={{ fontSize: '11px', color: isInteractive ? '#9ca3af' : '#f59e0b' }}
            />
            <Switch
              size="small"
              checked={isInteractive}
              onChange={handleModeToggle}
              disabled={loading}
              style={{ backgroundColor: isInteractive ? '#6366f1' : undefined }}
            />
            <MessageOutlined
              style={{ fontSize: '11px', color: isInteractive ? '#6366f1' : '#9ca3af' }}
            />
          </div>
        </Tooltip>
      </div>

      {/* Message history */}
      {hasMessages && (
        <div
          style={{
            maxHeight: '300px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            padding: '6px',
            background: '#f9fafb',
            borderRadius: '6px',
            border: '1px solid #e5e7eb',
          }}
        >
          {messages.map(msg => (
            <div
              key={msg.id}
              style={{
                display: 'flex',
                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              }}
            >
              <div
                style={{
                  maxWidth: '88%',
                  padding: '5px 9px',
                  borderRadius: '8px',
                  fontSize: '11px',
                  lineHeight: 1.45,
                  whiteSpace: 'pre-line',
                  background:
                    msg.role === 'user'
                      ? '#6366f1'
                      : msg.type === 'error'
                      ? '#fee2e2'
                      : '#ffffff',
                  color:
                    msg.role === 'user'
                      ? '#ffffff'
                      : msg.type === 'error'
                      ? '#dc2626'
                      : '#1f2937',
                  border: msg.role === 'assistant' ? '1px solid #e5e7eb' : 'none',
                  fontStyle: msg.type === 'source_text' && msg.role === 'user' ? 'italic' : 'normal',
                }}
              >
                {msg.text}
                {msg.type === 'analysis_result' && msg.serviceItems && msg.serviceItems.length > 0 && (
                  <div style={{ marginTop: '6px' }}>
                    {msg.serviceItems.map((si: any, siIdx: number) => (
                      si.procedure_steps && si.procedure_steps.length > 0
                        ? (
                          <ProcedureStepsDisplay
                            key={siIdx}
                            steps={si.procedure_steps}
                            totalHours={si.labor_hours ?? 0}
                            onStepHoursChange={(newTotal) => {
                              onServiceItemChange(siIdx, 'labor_hours', newTotal);
                            }}
                          />
                        )
                        : null
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Focused field follow-up suggestion for most recent assistant message */}
          {(() => {
            const lastAI = [...messages].reverse().find(m => m.role === 'assistant' && m.follow_up);
            return lastAI?.follow_up ? (
              <div
                style={{
                  fontSize: '11px',
                  color: '#6366f1',
                  padding: '4px 8px',
                  background: '#eff6ff',
                  borderRadius: '6px',
                  border: '1px solid #bfdbfe',
                }}
              >
                {lastAI.follow_up}
              </div>
            ) : null;
          })()}

          <div ref={bottomRef} />
        </div>
      )}

      {/* Focused field context pill (fast mode, after analysis) */}
      {!isInteractive && hasAnalyzed && focusHintLabel && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ fontSize: '10px', color: '#6b7280' }}>Context:</span>
          <Tag
            color="geekblue"
            style={{ fontSize: '10px', lineHeight: '16px', padding: '0 5px', margin: 0 }}
          >
            {focusHintLabel}
          </Tag>
        </div>
      )}

      {/* Input row */}
      <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-end' }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <TextArea
            value={input}
            onChange={e => setInput(e.target.value.slice(0, 2000))}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            autoSize={{ minRows: hasAnalyzed ? 1 : 2, maxRows: hasAnalyzed ? 4 : 4 }}
            disabled={loading || disabled}
            style={{
              fontSize: '12px',
              resize: 'none',
              paddingBottom: input.length > 1800 ? '14px' : undefined,
            }}
          />
          {input.length > 1800 && (
            <span
              style={{
                position: 'absolute',
                bottom: '4px',
                right: '8px',
                fontSize: '10px',
                color: input.length >= 2000 ? '#ef4444' : '#f59e0b',
                pointerEvents: 'none',
              }}
            >
              {input.length}/2000
            </span>
          )}
        </div>
        <Button
          type="primary"
          size="small"
          icon={loading ? <Spin size="small" /> : <SendOutlined />}
          onClick={handleSend}
          disabled={!input.trim() || loading || disabled || input.length >= 2000}
          style={{
            background: '#6366f1',
            borderColor: '#6366f1',
            minWidth: '32px',
          }}
        />
      </div>

      {/* Hint text when empty and not yet analyzed */}
      {!hasAnalyzed && !hasMessages && (
        <p style={{ fontSize: '10px', color: '#c4b5fd', margin: 0, lineHeight: 1.3 }}>
          {isInteractive ? 'Guided mode: I will ask questions.' : 'Fast mode: paste notes and press Enter.'}
        </p>
      )}
    </div>
  );
};

export default UnifiedAIChat;
